/**
 * <pi-cart-pair-upsells>
 * Native scroll-snap "Pair with your order" slider for the theme cart drawer.
 * Add via native fetch, then cart:refresh (Impact-safe).
 */
(() => {
  if (customElements.get('pi-cart-pair-upsells')) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const nativeFetch = (...args) => (window.__piThemeCart?.nativeFetch || window.__nativeFetch || window.fetch)(...args);

  class PiCartPairUpsells extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.track = this.querySelector('[data-pi-pair-track]');
      this.prevBtn = this.querySelector('[data-pi-pair-prev]');
      this.nextBtn = this.querySelector('[data-pi-pair-next]');

      this._onPrev = () => this._scrollBy(-1);
      this._onNext = () => this._scrollBy(1);
      this._onScroll = () => this._syncNav();
      this._onClick = this._onClick.bind(this);
      this._onResize = () => this._syncNav();

      this.prevBtn?.addEventListener('click', this._onPrev);
      this.nextBtn?.addEventListener('click', this._onNext);
      this.track?.addEventListener('scroll', this._onScroll, { passive: true });
      this.addEventListener('click', this._onClick);
      window.addEventListener('resize', this._onResize, { passive: true });

      if (typeof ResizeObserver === 'function' && this.track) {
        this._ro = new ResizeObserver(() => this._syncNav());
        this._ro.observe(this.track);
      }

      requestAnimationFrame(() => this._syncNav());
    }

    disconnectedCallback() {
      this.prevBtn?.removeEventListener('click', this._onPrev);
      this.nextBtn?.removeEventListener('click', this._onNext);
      this.track?.removeEventListener('scroll', this._onScroll);
      this.removeEventListener('click', this._onClick);
      window.removeEventListener('resize', this._onResize);
      this._ro?.disconnect();
      this._bound = false;
    }

    _cards() {
      return Array.from(this.querySelectorAll('[data-pi-pair-card]'));
    }

    _scrollBy(dir) {
      if (!this.track) return;
      const card = this._cards()[0];
      const gap = 16;
      const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.9;
      this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    _syncNav() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const left = this.track.scrollLeft;
      if (this.prevBtn) this.prevBtn.disabled = left <= 2;
      if (this.nextBtn) this.nextBtn.disabled = left >= max;
      const showNav = this.track.scrollWidth > this.track.clientWidth + 4;
      this.classList.toggle('pi-cart-pair--scrollable', showNav);
    }

    async _onClick(event) {
      const btn = event.target.closest?.('[data-pi-pair-add]');
      if (!btn || !this.contains(btn) || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;

      event.preventDefault();
      const variantId = Number(btn.getAttribute('data-variant-id') || 0);
      if (!variantId) return;

      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;

      try {
        const addRes = await nativeFetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!addRes.ok) throw new Error('add failed');

        const api = window.__piThemeCart;
        if (api?.refreshAndOpenDrawer) {
          await api.refreshAndOpenDrawer();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          api?.scheduleOpenDrawer?.();
        }
      } catch (error) {
        console.warn('[pi-cart-pair] add failed', error);
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
      }
    }
  }

  customElements.define('pi-cart-pair-upsells', PiCartPairUpsells);
})();
